/**
 * 统一埋点（客户端）。
 *
 * ══ 采什么 ══
 *   应用启动性能、路由到达与「内容可见耗时」、关键动作、产品漏斗、接口与前端异常。
 *   事件契约与白名单在服务端（apps/api/src/core/analytics.ts），前端只能发已登记的事件。
 *
 * ══ 绝不采什么 ══
 *   **任何身份信息与健康数据**：手机号、姓名、成员编号之外的标识、检验值、报告原文、诊断文本。
 *   服务端会逐条拒绝这类载荷，所以这里不能靠「忘了传」来保证合规 —— 传了也进不去。
 *   属性值一律使用机器可读标识（`section: 'tri_helix'`），不传展示文案。
 *
 * ══ 为什么不影响产品 ══
 *   上报是「尽力而为」：失败只重试有限次，队列有上限，任何异常都在本模块内被吞掉。
 *   埋点绝不能让一次页面操作失败或变慢。
 */
import { getToken } from './api.ts';

interface QueuedEvent {
  id: string;
  name: string;
  route: string;
  session_id: string;
  client_at: string;
  props?: Record<string, string | number | boolean>;
}

const CONFIG_PATH = '/api/analytics/config';
const EVENTS_PATH = '/api/analytics/events';
const maxQueue = 120;
const maxBatch = 50;
const flushIntervalMs = 5000;
/** 连续失败上限：超过后丢弃本批，避免弱网下无限重试把内存堆满 */
const maxFlushAttempts = 3;

const queue: QueuedEvent[] = [];
let enabled = true;
let flushAttempts = 0;
let timer: number | null = null;
let initialized = false;
let configChecked = false;

function uuid(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  } catch {
    /* 忽略：降级到下面的随机串 */
  }
  return `e${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
}

const sessionId = uuid();

function enqueue(name: string, route: string, props?: Record<string, string | number | boolean>): void {
  if (!enabled) return;
  if (queue.length >= maxQueue) queue.shift(); // 有界队列：丢最旧的，不阻塞
  queue.push({
    id: uuid(),
    name,
    route,
    session_id: sessionId,
    client_at: new Date().toISOString(),
    ...(props ? { props } : {}),
  });
  schedule();
}

function schedule(): void {
  if (timer !== null) return;
  timer = window.setTimeout(() => {
    timer = null;
    void flush('interval');
  }, flushIntervalMs);
}

/** 尽力上报。任何失败都只影响埋点本身。 */
async function flush(reason: 'interval' | 'hide' | 'manual'): Promise<void> {
  if (!enabled || queue.length === 0) return;
  const batch = queue.splice(0, maxBatch);
  const token = getToken();

  try {
    const response = await fetch(EVENTS_PATH, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        ...(token ? { authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ events: batch, platform: 'web' }),
      // 页面隐藏时用 keepalive，让请求在页面卸载后仍能完成
      keepalive: reason === 'hide',
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    flushAttempts = 0;
  } catch {
    flushAttempts++;
    if (flushAttempts <= maxFlushAttempts) {
      // 放回队列头部保持顺序，下次再试（总长度仍受上限约束）
      queue.unshift(...batch);
      while (queue.length > maxQueue) queue.pop();
    } else {
      // 放弃本批：埋点数据可以丢，但不能拖累产品
      flushAttempts = 0;
    }
  }
}

/** 查询一次服务端开关；关闭时立刻停止采集并清空队列。 */
async function syncConfig(): Promise<void> {
  if (configChecked) return;
  configChecked = true;
  try {
    const response = await fetch(CONFIG_PATH);
    if (!response.ok) return;
    const data = (await response.json()) as { enabled?: boolean };
    if (data.enabled === false) {
      enabled = false;
      queue.length = 0;
    }
  } catch {
    // 取不到配置就按「开启」继续：服务端仍会兜底拒绝
  }
}

/**
 * 初始化：上报启动性能，注册页面隐藏时的冲刷。
 * 只能调用一次（多次调用是安全的，但不会重复上报）。
 */
export function initAnalytics(): void {
  if (initialized) return;
  initialized = true;
  void syncConfig();

  // 应用启动性能（Navigation Timing）
  try {
    const entries = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
    const nav = entries[0];
    if (nav && nav.duration > 0) {
      enqueue('app_boot', '', {
        ms: Math.round(nav.duration),
        ttfb_ms: Math.round(nav.responseStart),
        dom_ms: Math.round(nav.domContentLoadedEventEnd),
        platform: 'web',
      });
    }
  } catch {
    /* 不支持 Navigation Timing 就跳过，不要因此影响启动 */
  }

  // 页面隐藏/卸载时冲刷一次，减少「最后几秒的事件丢失」
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') void flush('hide');
  });
  window.addEventListener('pagehide', () => void flush('hide'));
}

/** 进入路由。 */
export function trackPageView(route: string, from?: string): void {
  enqueue('page_view', route, from ? { from } : undefined);
}

/**
 * 路由内容可见。
 * 定义：从进入路由到「该路由的取数全部结束并完成一帧绘制」的耗时 ——
 * 这正是「用户多久能看到内容」的可测代理指标，也是回答「3 秒建立认知」的依据。
 */
export function trackPageReady(route: string, ms: number): void {
  enqueue('page_ready', route, { ms: Math.max(0, Math.round(ms)) });
}

/** 关键动作。`name` 与 `target` 只能用机器可读标识（不含中文与个人信息）。 */
export function trackAction(name: string, target?: string): void {
  const route = typeof window === 'undefined' ? '' : window.location.pathname;
  enqueue('action', route, target ? { name, target } : { name });
}

/** 产品漏斗步骤。 */
export function trackFunnel(funnel: string, step: string): void {
  const route = typeof window === 'undefined' ? '' : window.location.pathname;
  enqueue('funnel_step', route, { funnel, step });
}

/** 接口错误：用于发现体验断点（只记路由、状态码与接口路径，不含业务数据）。 */
export function trackApiError(route: string, status: number, endpoint?: string): void {
  enqueue('api_error', route, endpoint ? { status, endpoint } : { status });
}

/** 供自测与排查：当前队列长度与开关状态。 */
export function analyticsDebug(): { enabled: boolean; queued: number; sessionId: string } {
  return { enabled, queued: queue.length, sessionId };
}

/** 立即冲刷（自测用）。 */
export function flushAnalytics(): Promise<void> {
  return flush('manual');
}
